import { useRef } from "react";

import { useText } from "../i18n.js";
import { KEY_STEP, clampWidth, type WidthBounds } from "../panes.js";
import { DIALOGS } from "../text/app.js";

interface Props {
  readonly label: string;
  readonly value: number;
  readonly bounds: WidthBounds;
  /** Which way the pane grows when the handle is dragged right: `right` for the sidebar, `left` for a pane on the right edge. */
  readonly grows: "right" | "left";
  readonly onChange: (width: number) => void;
}

/**
 * The strip on a pane's edge that drags it wider or narrower. Arrow keys move
 * it too, and a double click puts the pane back to its starting width.
 */
export function Resizer({ label, value, bounds, grows, onChange }: Props): React.JSX.Element {
  const t = useText(DIALOGS);
  const start = useRef<{ x: number; width: number } | null>(null);
  const sign = grows === "right" ? 1 : -1;
  return (
    <div
      className={`resizer resizer-${grows}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={bounds.min}
      aria-valuemax={bounds.max}
      tabIndex={0}
      title={t.resize}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        start.current = { x: event.clientX, width: value };
        document.body.classList.add("resizing");
      }}
      onPointerMove={(event) => {
        const from = start.current;
        if (from == null) return;
        onChange(clampWidth(bounds, from.width + sign * (event.clientX - from.x)));
      }}
      onPointerUp={() => {
        start.current = null;
        document.body.classList.remove("resizing");
      }}
      onPointerCancel={() => {
        start.current = null;
        document.body.classList.remove("resizing");
      }}
      onDoubleClick={() => onChange(bounds.initial)}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") onChange(clampWidth(bounds, value - sign * KEY_STEP));
        else if (event.key === "ArrowRight") onChange(clampWidth(bounds, value + sign * KEY_STEP));
        else if (event.key === "Home") onChange(bounds.min);
        else if (event.key === "End") onChange(bounds.max);
        else return;
        event.preventDefault();
      }}
    />
  );
}
